import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Link } from 'react-router';
import classNames from 'classnames';

import EPGItem from 'screens/Live/components/EPGItem';
import CallSign from 'components/CallSign';
import * as Logos from 'components/CallSign/images';
import { sportsFeatures, isSportsContent } from 'helpers/ContentHelper';
import {
  MINUTES_INTERVAL,
  PANEL_TYPE_LISTING,
  displayPrimeTimeMessage,
  checkForOverlap,
  isVideoLive,
  getLocalNetworkUrl,
} from 'screens/Live/LiveHelpers';
import { now, stringToDate, isAfter } from 'helpers/DateTimeHelper';
import { Element } from 'react-scroll';
import LiveImage from './components/LiveImage';
import Style from './EPGRow.module.scss';

const EPGRow = ({
  account,
  atStartDate,
  breakpoint,
  breakpointIsMobile,
  defaultProgram,
  endingDate,
  epgGridPage,
  favoritesManager,
  globalVideo,
  gridStart,
  isAuthenticated,
  location,
  overlay,
  preauthorizedResources,
  row,
  rowIdx,
  saveLiveRestart,
  selectedChannel,
  selectedDate,
  selectedVideoData,
  setCurrentVideo,
  setOverlay,
  startingTs,
}) => {
  const { panelType } = row || {};
  const items = row?.items?.member;
  if (_.isEmpty(items)) return null;
  const firstItem = row?.rows?.member?.[0];
  const currentItem = row?.items?.member?.find(video => isVideoLive(video));
  const { startDate, endDate } = currentItem || {};
  const currentImage = currentItem?.images?.seriesList?.raw;

  const hasSports = firstItem && isSportsContent(firstItem);

  if (panelType !== PANEL_TYPE_LISTING || (!sportsFeatures() && hasSports)) return null;

  const { callSign } = row;
  const network = row?.items?.member?.[0]?.network ?? null;

  const { lateProgramIdx, overFlowProgramIdx, overFlowPrograms, liveListing } = checkForOverlap(
    row,
  );

  let scheduleRowClassName = Style.scheduleRow;

  scheduleRowClassName =
    (!selectedChannel && rowIdx === 0) || rowIdx === selectedChannel
      ? classNames(scheduleRowClassName, Style.scheduleRowSelected)
      : scheduleRowClassName;

  const epgItemProps = {
    setOverlay,
    overlay,
    network,
    account,
    startingTs,
    callSign,
    selectedDate,
    displayPrimeTimeMessage,
    epgGridPage,
    endingDate,
    gridStart,
    preauthorizedResources,
    isAuthenticated,
    breakpoint,
    saveLiveRestart,
    breakpointIsMobile,
    lateProgramIdx,
    overFlowProgramIdx,
    selectedVideoData,
    favoritesManager,
    location,
    setCurrentVideo,
  };

  const programs = overFlowPrograms || [];
  const overflowRows = [];
  let overflowCount = 0;

  const scheduleLeftClasses = !atStartDate
    ? classNames(Style.scheduleLeft, Style.scheduleLeftNotFirst)
    : Style.scheduleLeft;

  if (sportsFeatures()) {
    let overFlowScheduleRowClassName = null;
    let overFlowRowIdx = 0;
    programs.forEach(overFlowProgram => {
      overFlowRowIdx += 1;
      epgItemProps.overFlowProgramIdx = overFlowRowIdx;
      const overFlowItem = _.cloneDeep(row);
      const endDateIsAfter = isAfter(stringToDate(overFlowProgram.endDate), selectedDate);
      const isCurrentLiveListingOver = liveListing && isAfter(now(), liveListing?.endDate);

      if (endDateIsAfter) {
        overFlowItem.items.member = [_.cloneDeep(overFlowProgram)];

        overFlowScheduleRowClassName = Style.scheduleRow;

        if (
          defaultProgram === overFlowProgram.id ||
          (selectedVideoData && overFlowProgram.id === selectedVideoData.id)
        ) {
          overFlowScheduleRowClassName = classNames(
            overFlowScheduleRowClassName,
            Style.scheduleRowSelected,
          );
        }
        const { endDate, startDate } = overFlowProgram || {};
        const overflowImage = overFlowProgram?.images?.seriesList?.raw;

        overflowRows.push(
          <div
            key={`schedulerow-${rowIdx}-${overFlowRowIdx}`}
            className={overFlowScheduleRowClassName}
          >
            <div className={Style.scheduleLeftContainer}>
              <Link className={scheduleLeftClasses} onlyActiveOnIndex>
                <CallSign
                  callSign={isCurrentLiveListingOver && overflowCount === 0 ? callSign : 'FEED'}
                  color="white"
                  style={Style}
                />
              </Link>
              <div className={Style.liveIndicator} />
            </div>
            <LiveImage
              image={overflowImage}
              shouldRender={atStartDate}
              startDate={startDate}
              endDate={endDate}
              isOverflow
            />
            <Element className={Style.scheduleItemWrapper} name={`${rowIdx}-${overFlowRowIdx}`}>
              <EPGItem
                {...epgItemProps}
                globalVideo={globalVideo}
                isOverFlow
                item={overFlowItem}
                name="overflow"
                overFlowProgramIdx={`${rowIdx}-${overFlowRowIdx}`}
                rowIdx={`${rowIdx}-${overFlowRowIdx}`}
                startingTs={startingTs}
                totalMinutes={MINUTES_INTERVAL}
              />
            </Element>
          </div>,
        );
        overflowCount += 1;
      }
    });
  }

  const renderCallSign = (callSign, network) => {
    if (!callSign) return null;
    const channelName = Logos[callSign.replace('-', '')] || null;
    const affiliateLogo = getLocalNetworkUrl(network, callSign);

    const networkLogoUrl = channelName ? null : affiliateLogo;

    const defaultSize = 60;
    return !networkLogoUrl ? (
      <CallSign callSign={callSign} color="white" style={Style} />
    ) : (
      <img src={`${networkLogoUrl}?resize=*:${defaultSize}px`} alt={callSign} />
    );
  };

  return (
    <>
      <div key={`schedulerow-${rowIdx}`}>
        <div className={scheduleRowClassName}>
          <div className={Style.scheduleLeftContainer}>
            <Link className={scheduleLeftClasses} onlyActiveOnIndex>
              {renderCallSign(callSign, network)}
            </Link>
            <div className={Style.liveIndicator} />
          </div>
          <LiveImage
            image={currentImage}
            shouldRender={atStartDate}
            startDate={startDate}
            endDate={endDate}
          />
          <Element className={Style.scheduleItemWrapper} name={`${rowIdx}`}>
            <EPGItem
              {...epgItemProps}
              globalVideo={globalVideo}
              isOverFlow={false}
              item={row}
              rowIdx={rowIdx}
              startingTs={startingTs}
              totalMinutes={MINUTES_INTERVAL}
            />
          </Element>
        </div>
      </div>
      {overflowRows}
    </>
  );
};

EPGRow.propTypes = {
  account: PropTypes.shape({}),
  atStartDate: PropTypes.bool,
  breakpoint: PropTypes.string,
  breakpointIsMobile: PropTypes.bool,
  defaultProgram: PropTypes.string,
  epgGridPage: PropTypes.number,
  endingDate: PropTypes.instanceOf(Date),
  favoritesManager: PropTypes.shape({
    favorites: PropTypes.arrayOf(PropTypes.shape({})),
    setFavorites: PropTypes.func,
    deleteFavorites: PropTypes.func,
  }),
  globalVideo: PropTypes.func,
  gridStart: PropTypes.func,
  isAuthenticated: PropTypes.bool,
  isStatic: PropTypes.bool,
  location: PropTypes.shape({}),
  overlay: PropTypes.string,
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  row: PropTypes.shape({}),
  rowIdx: PropTypes.number,
  saveLiveRestart: PropTypes.func,
  selectedChannel: PropTypes.number,
  selectedDate: PropTypes.shape({}),
  selectedVideoData: PropTypes.shape({}),
  setOverlay: PropTypes.func,
  setSelectedDate: PropTypes.func,
  startingTs: PropTypes.number,
  setCurrentVideo: PropTypes.func,
};

export default EPGRow;
